import { Add, KeyboardArrowDown } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import * as React from 'react';
import { fetchHubWhatsappTemplates } from '../../api/customer/hub-templates.api';
import type { HubWhatsappTemplate } from '../../api/customer/hub-templates.api';
import type { WhatsAppConfig } from '../../hooks/useWhatsAppConfig';
import HubCreateTemplateDialog from './HubCreateTemplateDialog';

function statusRank(status: string | null | undefined): number {
    const s = String(status || '').toUpperCase();
    if (s === 'APPROVED') return 0;
    if (s === 'PENDING' || s === 'IN_APPEAL') return 1;
    if (s === 'PAUSED') return 2;
    return 3;
}

function statusHe(status: string | null | undefined): string {
    const s = String(status || '').toUpperCase();
    if (s === 'APPROVED') return 'מאושר';
    if (s === 'PENDING') return 'ממתין';
    if (s === 'IN_APPEAL') return 'בערעור';
    if (s === 'PAUSED') return 'מושהה';
    if (s === 'REJECTED') return 'נדחה';
    return s || '';
}

function sortTemplates(rows: HubWhatsappTemplate[]): HubWhatsappTemplate[] {
    return [...rows].sort((a, b) => {
        const rank = statusRank(a.status) - statusRank(b.status);
        if (rank !== 0) return rank;
        return String(a.name || '').localeCompare(String(b.name || ''));
    });
}

type Props = {
    waConfig: WhatsAppConfig | null;
    onSelect: (pattern: string) => void;
};

export default function HubWhatsappTemplatesButton({ waConfig, onSelect }: Props) {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [templates, setTemplates] = React.useState<HubWhatsappTemplate[] | null>(null);
    const [createOpen, setCreateOpen] = React.useState(false);
    const open = Boolean(anchorEl);

    const load = React.useCallback(async () => {
        const inboxId = String(waConfig?.instanceId || '').trim();
        if (!inboxId) {
            setError('אין inbox של ווטסאפ');
            setTemplates([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const rows = await fetchHubWhatsappTemplates(inboxId);
            setTemplates(sortTemplates(rows.filter((row) => String(row.name || '').trim())));
        } catch (e: any) {
            setTemplates([]);
            setError(e?.message || 'לא ניתן לטעון תבניות ווטסאפ');
        } finally {
            setLoading(false);
        }
    }, [waConfig?.instanceId]);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        void load();
    };

    const handleClose = () => setAnchorEl(null);

    const handlePick = (row: HubWhatsappTemplate) => {
        const pattern = String(row.pattern || '').trim();
        if (!pattern) return;
        onSelect(pattern);
        handleClose();
    };

    const handleAddNew = () => {
        handleClose();
        setCreateOpen(true);
    };

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={handleOpen}
                endIcon={loading ? <CircularProgress size={14} /> : <KeyboardArrowDown />}
                sx={{ whiteSpace: 'nowrap' }}
            >
                תבניות ווטסאפ
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: { minWidth: 280, maxHeight: 420, direction: 'rtl' },
                }}
            >
                <MenuItem onClick={handleAddNew}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                        <Add fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="הוסף תבנית חדשה" />
                </MenuItem>
                <Divider />
                {loading && !templates ? (
                    <MenuItem disabled>
                        <ListItemText primary="טוען תבניות…" />
                    </MenuItem>
                ) : null}
                {error ? (
                    <MenuItem disabled>
                        <ListItemText primary={error} />
                    </MenuItem>
                ) : null}
                {!loading && !error && templates && templates.length === 0 ? (
                    <MenuItem disabled>
                        <ListItemText primary="אין תבניות בחשבון זה" />
                    </MenuItem>
                ) : null}
                {templates?.map((row) => {
                    const status = String(row.status || '').toUpperCase();
                    const rejected = status === 'REJECTED';
                    const pattern = String(row.pattern || '').trim();
                    const title = String(row.name || '').trim();
                    const secondary = [
                        row.body_preview || null,
                        statusHe(row.status),
                        row.language,
                        !pattern && !rejected ? 'חסר pattern מה-Hub' : null,
                    ].filter(Boolean).join(' · ');
                    return (
                        <MenuItem
                            key={`${row.id || row.name}-${row.language || 'he'}`}
                            disabled={rejected || !pattern}
                            onClick={() => handlePick(row)}
                        >
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {secondary}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </MenuItem>
                    );
                })}
            </Menu>
            <HubCreateTemplateDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                channelAccountId={String(waConfig?.instanceId || '')}
                existingNames={(templates || []).map((row) => String(row.name || ''))}
                onCreated={(pattern) => {
                    setCreateOpen(false);
                    onSelect(pattern);
                    void load();
                }}
            />
        </>
    );
}
